import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { AlertService, UserService, AuthenticationService, ClientService } from '../_services/index';
import { Guid } from "guid-typescript";
import { CustomValidators } from '../_helpers/custom-validators';

import { AdministrationService } from '../_services/administration.service';
import { ThemeService } from '../_helpers/theming-engine/theme.service';
import { ErrorModalHelperService } from '../_helpers/error-modal-helper/error-modal-helper.service';
import { SuccessModalHelperService } from '../_helpers/success-modal-helper/success-modal-helper.service';
import { FeatureHelperService } from '../_helpers/feature-helper/feature-helper.service';
@Component({
    templateUrl: 'register.component.html',
    styleUrls: ['register.component.css'],
    standalone: false
})

export class RegisterComponent implements OnInit {
  /** A set-up or reset link opened again after it was used, or after a newer link replaced it (38760). */
  static readonly LINK_USED_MESSAGE = 'This link has already been used. Sign in with your email address and the password you set, '
    + 'or use Forgot password on the sign-in page. If you have been sent a newer link, use that one instead.';
  /** An expired link: the Resend activation code button shows below the message. */
  static readonly LINK_EXPIRED_MESSAGE = 'This link has expired. Use the Resend activation code button to have a new link emailed to you.';

  model: any = {};
  loading = false;
  enableBtnResend: boolean = false;
  activationCode: string;
  frmSignup: UntypedFormGroup;
  submitted = false;
  returnUrl: string;
  verifiedMfa: boolean = false;
  showPasswordButton: boolean = false
  showContinueButton: boolean = false;
  showMfaTextField: boolean = false;
  showResendMfa: boolean = false;
  currentRoletype: number;
  constructor(
    private route: ActivatedRoute,
    private formBuilder: UntypedFormBuilder,
    private router: Router,
    private userService: UserService,
    private authenticationService: AuthenticationService,
    private successModalHelper: SuccessModalHelperService,
    private administration: AdministrationService,
    private clientService: ClientService,
    public themeService: ThemeService,
    private errorModalHelper: ErrorModalHelperService,
    private featureHelperService?: FeatureHelperService) { }

  ngOnInit() {
    
    this.themeService.setActiveTheme('LEAP', false);

    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';
    this.getUsername();
    this.frmSignup = this.formBuilder.group({
      username: [''],
      password: [null, Validators.compose([
          Validators.required,
          // check whether the entered password has a number
          CustomValidators.patternValidator(/\d/, {
            hasNumber: true
          }),
          // check whether the entered password has upper case letter
          CustomValidators.patternValidator(/[A-Z]/, {
            hasCapitalCase: true
          }),
          // check whether the entered password has a lower case letter
          CustomValidators.patternValidator(/[a-z]/, {
            hasSmallCase: true
          }),
          // check whether the entered password has a special character
          CustomValidators.patternValidator(
            /[ !@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/,
            {
              hasSpecialCharacters: true
            }
          ),
          Validators.minLength(8)
        ])],
      confirmPassword: [null, Validators.compose([Validators.required])],
      mfaCode : [null]
      },
      {
        // check whether our password and confirm password match
        validator: CustomValidators.MustMatch
      });
  }

  get f() { return this.frmSignup.controls; }

  submit() {
    this.submitted = true;
    if (this.frmSignup.invalid) {
      return;
    }
    if (this.model.roleTypeId == 3 && this.showMfaTextField) {
      this.validateMfa();
    }
    else {
      this.submitData();
    }
  }

  submitData() {
    if (this.model.type === "PR") {
      this.resetPassword();
    }
    else {
      this.register();
    }
  }

  resendCode() {
    this.userService.resendActivationCode(this.activationCode).subscribe(data => {
      this.successModalHelper.show('New activation code has been resent');
    },
      error => {
        this.errorModalHelper.show(error.message, error);
      }
    )
  }

  getUsername() {
    this.route.queryParams.subscribe(par => {
      this.activationCode = par.activationCode;
      // `! par.activationCode == null` parses as `(!par.activationCode) == null`, which is false for
      // EVERY input -- a boolean is never loosely equal to null. So this guard could not be entered,
      // and every visit to /register fell through to the redirect below: the password-reset and
      // account-activation links both landed on the login page (bug 37019). It came in when the
      // deprecated isNullOrUndefined() helper was inlined; `!= null` is what that meant, and it
      // covers undefined too.
      if (par.activationCode != null && Guid.isGuid(par.activationCode)) {
        this.userService.getByAuthenticationId(par.activationCode).subscribe((data: any) => {
          
          this.model.username = data.username;
          this.model.type = data.type;
          this.model.roleTypeId = data.roleTypeId;
          this.model.userId = data.userId;
          this.currentRoletype = data.roleTypeId;
          
          this.showContinueButton = data.enableMfaAuthentication;
          this.model.buttonname = data.roleTypeId == 3 && data.enableMfaAuthentication ? "Get Started" : "Register";
          this.showPasswordButton = !this.showContinueButton;

          if (data.enableMfaAuthentication) {
            this.frmSignup.get('mfaCode').setValidators([Validators.required]);
            this.frmSignup.get('mfaCode').updateValueAndValidity();
          }

          this.model.header = "Register";
          if (this.model.type != null && this.model.type === 'PR') {
            this.model.buttonname = 'Reset Password';
            this.model.header = 'Reset Password';
          }
        },
          error => {
            if (error.message.includes('Activation code expired')) {
              this.errorModalHelper.show(RegisterComponent.LINK_EXPIRED_MESSAGE, error);
              this.enableBtnResend = true;
            }
            else if (error.message.includes('Invalid activation code') || error.message.includes('User is active')) {
              // The API names the code ("Invalid activation code -- <guid>"), which means nothing to the person
              // holding the link. Setting a password removes the code, so this is a link already used (38760).
              this.errorModalHelper.show(RegisterComponent.LINK_USED_MESSAGE, error);
            }
            else {
              this.errorModalHelper.show(error.message, error);
            }
          });
      }
      else {
        this.router.navigate(['/login']);
      }
    })
  }

  resetPassword() {
    this.userService.resetpassword(this.getFormValues())
      .subscribe(
        data => {
          this.successModalHelper.show('Password reset successful');
          this.authenticationService.login(this.frmSignup.value.username, this.frmSignup.value.password, 'LEAP')
            .subscribe(user => this.signInAs(user))
        },
        error => {
          this.errorModalHelper.show(error.message, error);
        });
  }

  register() {
    this.userService.register(this.getFormValues())
      .subscribe(
        data => {
          this.successModalHelper.show('Registration successful');
          this.authenticationService.login(this.frmSignup.value.username, this.frmSignup.value.password, 'LEAP')
            .subscribe(user => this.signInAs(user))
        },
        error => {
          this.errorModalHelper.show(error.message, error);
        });
  }

  /**
   * Keeps the sign-in that follows registering or resetting a password (bug 40309). login() only returns
   * the user, so without setUser() the auth guard found nobody signed in and sent them back to /login.
   * A user with MFA on still enters their code on the login page, as login.component does.
   */
  private signInAs(user: any) {
    if (user?.mfaEnabled) {
      return this.router.navigate(['/login'], { queryParams: { returnUrl: this.returnUrl } });
    }
    if (user.tutorIds && user.tutorIds[0] == null) {
      user.tutorIds = [];
    }
    this.featureHelperService?.setUserFeatures(user.userFeatures);
    this.authenticationService.setUser(user);
    return this.router.navigateByUrl(this.returnUrl);
  }

  getFormValues() {
    var formModel = this.frmSignup.value;
    formModel.activationcode = this.activationCode;
    formModel.username = this.model.username;
    return formModel;
  }

  generateMfa(type) {
    this.administration.createMfaCode(this.model.userId).subscribe(result => {
      
      this.showMfaTextField = result > 0;
      this.showContinueButton = false;
      this.showPasswordButton = true;
      if (type === 'resend') {
        this.successModalHelper.show("New authentication code has been sent");
      }
    }, error => {
        this.errorModalHelper.show("Error generating authentication code", error);
    });
  }

  validateMfa() {
    if (this.frmSignup.invalid) {
      return;
    }
    var data = {
      userId: this.model.userId,
      mfaCode: this.frmSignup.value.mfaCode
    };

    this.administration.validateMfaCode(data).subscribe(result => {
      this.submitData();
      this.successModalHelper.show("");
    }, error => {
      this.errorModalHelper.show(error.message, error);
    })
  }
}
